import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, LogOut, LayoutGrid, FileText, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import SEOMeta from "../../components/SEOMeta";

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm px-4 py-3 rounded-md outline-none focus:border-gold transition-colors duration-300";
const labelCls = "block font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2";

const FIELD_CONFIG = {
  home: [
    { name: "hero_line_1", label: "Hero Headline — Line 1" },
    { name: "hero_line_2", label: "Hero Headline — Line 2" },
    { name: "hero_subtext", label: "Hero Subtext", multiline: true },
    { name: "who_text", label: "Who We Are Text", multiline: true },
    { name: "approach_items", label: "Approach Items", list: true },
    { name: "why_items", label: "Why Touson Items", list: true },
    { name: "cta_title", label: "CTA Title" },
    { name: "cta_text", label: "CTA Text", multiline: true },
  ],
  about: [
    { name: "intro", label: "Introduction", multiline: true },
    { name: "vision", label: "Vision", multiline: true },
    { name: "mission", label: "Mission", multiline: true },
    { name: "values", label: "Values", list: true },
  ],
  approach_page: [
    { name: "intro", label: "Introduction", multiline: true },
    { name: "steps", label: "Approach Steps", list: true },
  ],
  businesses_page: [
    { name: "intro", label: "Introduction", multiline: true },
    { name: "explore_text", label: "Exploration Text", multiline: true },
  ],
  contact: [
    { name: "intro", label: "Introduction", multiline: true },
    { name: "email", label: "Contact Email" },
    { name: "address_lines", label: "Address Lines (one per line)", lines: true },
  ],
};

const KEY_LABELS = {
  home: "Home Page",
  about: "About Page",
  approach_page: "Approach Page",
  businesses_page: "Businesses Page",
  contact: "Contact Page",
};

const serialize = (value, field) => {
  if (field.list) return (value || []).map((i) => `${i.title}::${i.text}`).join("\n");
  if (field.lines) return (value || []).join("\n");
  return value ?? "";
};

const deserialize = (str, field) => {
  if (field.list)
    return str
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [title, ...rest] = l.split("::");
        return { title: title.trim(), text: rest.join("::").trim() };
      });
  if (field.lines)
    return str
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
  return str;
};

function BusinessesTab() {
  const [businesses, setBusinesses] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get("/admin/businesses").then((r) => setBusinesses(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const openModal = (biz) => {
    setForm(biz || { name: "", category: "", description: "", image_url: "", status: "active", order: businesses.length + 1 });
    setModal(biz ? biz.id : "new");
  };

  const save = async () => {
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (modal === "new") await api.post("/admin/businesses", payload);
      else await api.put(`/admin/businesses/${modal}`, payload);
      toast.success(modal === "new" ? "Business added" : "Business updated");
      setModal(null);
      load();
    } catch {
      toast.error("Failed to save business");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this business?")) return;
    try {
      await api.delete(`/admin/businesses/${id}`);
      toast.success("Business deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-businesses-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-white/50">{businesses.length} venture(s)</p>
        <button
          onClick={() => openModal(null)}
          data-testid="admin-add-business-button"
          className="inline-flex items-center gap-2 bg-gold text-navy text-sm font-medium px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors duration-300"
        >
          <Plus size={15} /> Add Business
        </button>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left">
              {["Order", "Name", "Category", "Status", "Actions"].map((h) => (
                <th key={h} className="px-5 py-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {businesses.map((b) => (
              <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-business-row-${b.id}`}>
                <td className="px-5 py-4 font-mono text-white/50">{b.order}</td>
                <td className="px-5 py-4 text-white">{b.name}</td>
                <td className="px-5 py-4 text-white/60">{b.category}</td>
                <td className="px-5 py-4">
                  <span className={`font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full border ${b.status === "active" ? "text-emerald-400 border-emerald-400/30" : "text-gold border-gold/30"}`}>
                    {b.status}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex gap-2">
                    <button onClick={() => openModal(b)} data-testid={`admin-edit-business-${b.id}`} className="p-2 text-white/50 hover:text-gold transition-colors" aria-label="Edit">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(b.id)} data-testid={`admin-delete-business-${b.id}`} className="p-2 text-white/50 hover:text-red-400 transition-colors" aria-label="Delete">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {businesses.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-white/40">
                  No businesses yet. Add your first one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-navy/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-lg bg-navy-800 border border-white/10 p-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-business-modal"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-display text-xl text-white">{modal === "new" ? "Add Business" : "Edit Business"}</h3>
                <button onClick={() => setModal(null)} className="text-white/50 hover:text-white" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-5">
                <div>
                  <label className={labelCls}>Name *</label>
                  <input data-testid="admin-biz-name-input" className={inputCls} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Category</label>
                    <input data-testid="admin-biz-category-input" className={inputCls} value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Technology" />
                  </div>
                  <div>
                    <label className={labelCls}>Order</label>
                    <input data-testid="admin-biz-order-input" type="number" className={inputCls} value={form.order ?? 0} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select data-testid="admin-biz-status-select" className={inputCls} value={form.status || "active"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="active" className="bg-navy-800">Active</option>
                    <option value="upcoming" className="bg-navy-800">Upcoming / Coming Soon</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Image URL</label>
                  <input data-testid="admin-biz-image-input" className={inputCls} value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
                </div>
                <div>
                  <label className={labelCls}>Description</label>
                  <textarea data-testid="admin-biz-description-input" rows={4} className={`${inputCls} resize-none`} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <button onClick={save} disabled={!form.name?.trim()} data-testid="admin-biz-save-button" className="w-full bg-gold text-navy font-medium text-sm py-3.5 rounded-full hover:bg-gold-light transition-colors duration-300 disabled:opacity-50">
                  Save Business
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ContentTab() {
  const [key, setKey] = useState("home");
  const [all, setAll] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/content").then((r) => setAll(r.data)).catch(() => toast.error("Failed to load content"));
  }, []);

  useEffect(() => {
    if (!all) return;
    const data = all[key] || {};
    const initial = {};
    FIELD_CONFIG[key].forEach((f) => {
      initial[f.name] = serialize(data[f.name], f);
    });
    setForm(initial);
  }, [key, all]);

  const save = async () => {
    setSaving(true);
    try {
      const data = {};
      FIELD_CONFIG[key].forEach((f) => {
        data[f.name] = deserialize(form[f.name] || "", f);
      });
      await api.put(`/admin/content/${key}`, data);
      setAll({ ...all, [key]: data });
      toast.success(`${KEY_LABELS[key]} content updated`);
    } catch {
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  if (!all) return <p className="text-white/40 text-sm">Loading content...</p>;

  return (
    <div data-testid="admin-content-tab">
      <div className="flex flex-wrap gap-2 mb-8">
        {Object.keys(FIELD_CONFIG).map((k) => (
          <button
            key={k}
            onClick={() => setKey(k)}
            data-testid={`admin-content-key-${k}`}
            className={`font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
              key === k ? "bg-gold text-navy border-gold" : "text-white/50 border-white/15 hover:border-gold/50 hover:text-gold"
            }`}
          >
            {KEY_LABELS[k]}
          </button>
        ))}
      </div>
      <div className="space-y-6 max-w-3xl">
        {FIELD_CONFIG[key].map((f) => (
          <div key={f.name}>
            <label className={labelCls}>{f.label}</label>
            {f.multiline || f.list || f.lines ? (
              <textarea
                data-testid={`admin-content-field-${f.name}`}
                rows={f.list ? 6 : 4}
                className={`${inputCls} resize-y font-normal`}
                value={form[f.name] || ""}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                placeholder={f.list ? "Title::Description (one item per line)" : ""}
              />
            ) : (
              <input
                data-testid={`admin-content-field-${f.name}`}
                className={inputCls}
                value={form[f.name] || ""}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
              />
            )}
            {f.list && <p className="mt-1.5 text-[11px] text-white/30">Format: Title::Description — one item per line</p>}
          </div>
        ))}
        <button onClick={save} disabled={saving} data-testid="admin-content-save-button" className="bg-gold text-navy font-medium text-sm px-8 py-3.5 rounded-full hover:bg-gold-light transition-colors duration-300 disabled:opacity-50">
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function EnquiriesTab() {
  const [enquiries, setEnquiries] = useState(null);

  useEffect(() => {
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => setEnquiries([]));
  }, []);

  if (!enquiries) return <p className="text-white/40 text-sm">Loading enquiries...</p>;

  return (
    <div data-testid="admin-enquiries-tab">
      <p className="text-sm text-white/50 mb-6">{enquiries.length} enquiry(ies)</p>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left">
              {["Date", "Name", "Email", "Phone", "Type", "Message"].map((h) => (
                <th key={h} className="px-5 py-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {enquiries.map((e) => (
              <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02] align-top" data-testid={`admin-enquiry-row-${e.id}`}>
                <td className="px-5 py-4 font-mono text-xs text-white/50 whitespace-nowrap">
                  {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </td>
                <td className="px-5 py-4 text-white whitespace-nowrap">{e.name}</td>
                <td className="px-5 py-4 text-gold">{e.email}</td>
                <td className="px-5 py-4 text-white/60">{e.phone || "—"}</td>
                <td className="px-5 py-4 text-white/60 whitespace-nowrap">{e.category}</td>
                <td className="px-5 py-4 text-white/60 max-w-xs"><span className="line-clamp-3">{e.message}</span></td>
              </tr>
            ))}
            {enquiries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-white/40">
                  No enquiries yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TABS = [
  { id: "businesses", label: "Businesses", icon: LayoutGrid },
  { id: "content", label: "Site Content", icon: FileText },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState("businesses");

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/auth/me")
      .then((r) => {
        if (!r.data.is_admin) {
          navigate("/admin/login", { replace: true });
          return;
        }
        setUser(r.data);
        setChecking(false);
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate, location.state]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    navigate("/", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center" data-testid="admin-loading">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy text-foreground" data-testid="admin-dashboard">
      <SEOMeta title="Admin Console | Touson Ventures" description="Touson Ventures admin console." />
      <header className="border-b border-white/10 bg-navy-800/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg tracking-tight text-white">
              Touson<span className="text-gold">.</span>
            </span>
            <span className="font-mono text-[9px] tracking-[0.3em] text-gold uppercase">Admin</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden sm:block text-xs text-white/50" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-gold transition-colors duration-300"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex gap-2 mb-10 border-b border-white/10 pb-px">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 -mb-px transition-colors duration-300 ${
                  tab === t.id ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"
                }`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>
        {tab === "businesses" && <BusinessesTab />}
        {tab === "content" && <ContentTab />}
        {tab === "enquiries" && <EnquiriesTab />}
      </div>
    </div>
  );
}
